import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';

export type AnnouncementBarHeight = 'slim' | 'normal' | 'wide';
export type RotationUnit = 'seconds' | 'minutes';
export type TimerUnit = 'minutes' | 'hours';
export type TimerFormat = 'digital' | 'compact';
export type ExpirationBehavior = 'show_expired_message' | 'hide_message';

export interface MessageFormShape {
  html: FormControl<string>;
  textColor: FormControl<string>;
}

export interface AnnouncementBarFormShape {
  height: FormControl<AnnouncementBarHeight>;
  backgroundColor: FormControl<string>;
  border: FormGroup<{
    enabled: FormControl<boolean>;
    color: FormControl<string>;
  }>;
  messages: FormArray<FormGroup<MessageFormShape>>;
  messageRotation: FormGroup<{
    value: FormControl<number>;
    unit: FormControl<RotationUnit>;
  }>;
  timer: FormGroup<{
    enabled: FormControl<boolean>;
    duration: FormGroup<{
      value: FormControl<number>;
      unit: FormControl<TimerUnit>;
    }>;
    format: FormControl<TimerFormat>;
    expirationBehavior: FormControl<ExpirationBehavior>;
    expiredMessage: FormControl<string>;
  }>;
  customCode: FormGroup<CustomCodeFormShape>;
}

interface MessageDefault {
  html: string;
  /** Empty means inherit the theme's colour — what messages did before this control existed. */
  textColor?: string;
}

const DEFAULTS = {
  height: 'slim' as AnnouncementBarHeight,
  backgroundColor: '#F5F5F5',
  border: { enabled: true, color: '#EEEEEE' },
  messages: [{ html: '' }, { html: '' }] as MessageDefault[],
  messageRotation: { value: 5, unit: 'seconds' as RotationUnit },
  timer: {
    enabled: false,
    duration: { value: 15, unit: 'minutes' as TimerUnit },
    format: 'digital' as TimerFormat,
    expirationBehavior: 'show_expired_message' as ExpirationBehavior,
    expiredMessage: 'Offer expired',
  },
};

export const ANNOUNCEMENT_BAR_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{backgroundColor}}',
    type: 'String',
    description: "Hex value from the admin's background color picker",
  },
  { reference: '{{border.enabled}}', type: 'Boolean', description: 'True when the border toggle is on in the admin' },
  { reference: '{{border.color}}', type: 'String', description: "Hex value from the admin's border color picker" },
  { reference: '{{messages}}', type: 'Array', description: 'All messages configured in the admin' },
  {
    reference: '{{messages[i].textColor}}',
    type: 'String',
    description: "Hex of that message's text colour; empty means inherit from the theme",
  },
  {
    reference: '{{messages[i].html}}',
    type: 'String',
    description: 'Rich-text HTML of one message — render with triple braces to preserve formatting',
  },
  {
    reference: '{{timer.enabled}}',
    type: 'Boolean',
    description: 'True when the countdown timer is turned on in the admin',
  },
  {
    reference: '{{timer.duration.value}}',
    type: 'Number',
    description: 'Configured timer duration value from the admin (e.g. 15)',
  },
  {
    reference: '{{timer.duration.unit}}',
    type: 'String',
    description: "Configured timer duration unit from the admin: 'minutes' or 'hours'",
  },
  { reference: '{{timer.expiredMessage}}', type: 'String', description: 'Text shown after the countdown reaches zero' },
  {
    reference: '{{countdown.formatted}}',
    type: 'String',
    description: 'Live ticking countdown, pre-formatted (e.g. "01:23:45")',
  },
  {
    reference: '{{countdown.remainingMs}}',
    type: 'Number',
    description: 'Live milliseconds remaining on the timer — useful for custom countdown formatting',
  },
  { reference: '{{countdown.expired}}', type: 'Boolean', description: 'True once the timer finishes counting down' },
];

export const ANNOUNCEMENT_BAR_STARTER_TEMPLATE = `<div
  style="background-color: {{backgroundColor}};{{#if border.enabled}} border: 1px solid {{border.color}};{{/if}}"
>
  {{#if timer.enabled}}
    {{#if countdown.expired}}
      <span class="expired">{{timer.expiredMessage}}</span>
    {{else}}
      <span class="countdown">{{countdown.formatted}}</span>
    {{/if}}
  {{/if}}

  {{#each messages}}
    <div class="message">{{{html}}}</div>
  {{/each}}
</div>`;

export const buildMessageGroup = (msg: MessageDefault): FormGroup<MessageFormShape> =>
  new FormGroup<MessageFormShape>({
    html: new FormControl(msg.html, { nonNullable: true }),
    // Saved messages predate this control, so an absent colour has to keep meaning "inherit".
    textColor: new FormControl(msg.textColor ?? '', { nonNullable: true }),
  });

export function createAnnouncementBarFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<AnnouncementBarFormShape> {
  const formGroup = new FormGroup<AnnouncementBarFormShape>({
    height: new FormControl(DEFAULTS.height, { nonNullable: true }),
    backgroundColor: new FormControl(DEFAULTS.backgroundColor, { nonNullable: true }),
    border: new FormGroup({
      enabled: new FormControl(DEFAULTS.border.enabled, { nonNullable: true }),
      color: new FormControl(DEFAULTS.border.color, { nonNullable: true }),
    }),
    messages: new FormArray<FormGroup<MessageFormShape>>(DEFAULTS.messages.map(buildMessageGroup)),
    messageRotation: new FormGroup({
      value: new FormControl(DEFAULTS.messageRotation.value, { nonNullable: true }),
      unit: new FormControl(DEFAULTS.messageRotation.unit, { nonNullable: true }),
    }),
    timer: new FormGroup({
      enabled: new FormControl(DEFAULTS.timer.enabled, { nonNullable: true }),
      duration: new FormGroup({
        value: new FormControl(DEFAULTS.timer.duration.value, { nonNullable: true }),
        unit: new FormControl(DEFAULTS.timer.duration.unit, { nonNullable: true }),
      }),
      format: new FormControl(DEFAULTS.timer.format, { nonNullable: true }),
      expirationBehavior: new FormControl(DEFAULTS.timer.expirationBehavior, { nonNullable: true }),
      expiredMessage: new FormControl(DEFAULTS.timer.expiredMessage, { nonNullable: true }),
    }),
    customCode: createCustomCodeFormGroup(undefined, ANNOUNCEMENT_BAR_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    const { messages: initialMessages, ...rest } = initialSettings as { messages?: unknown };
    if (Array.isArray(initialMessages)) {
      formGroup.controls.messages.clear();
      for (const raw of initialMessages) {
        if (typeof raw === 'string') {
          formGroup.controls.messages.push(buildMessageGroup({ html: raw }));
        } else if (raw && typeof raw === 'object') {
          const msg = raw as { html?: unknown; textColor?: unknown };
          formGroup.controls.messages.push(
            buildMessageGroup({
              html: typeof msg.html === 'string' ? msg.html : '',
              textColor: typeof msg.textColor === 'string' ? msg.textColor : '',
            }),
          );
        }
      }
    }
    formGroup.patchValue(rest, { emitEvent: false });
  }

  return formGroup;
}
